/**
 * What the assistant is told about /channels/:platform: a LookerChatContext
 * (R21) with template.id "channels", read by the bubble in mode 'channels'
 * with CHANNEL_ADVISOR, whose page rules explain its keys (spec 2026-10-20
 * page skills §2.3). Built from the tables the file writes (R22); cells are
 * shown as the page renders them. A breakdown tab's "รวม" line travels as
 * `totals`, so the row cap never cuts it. Failed and empty boxes are told as
 * such; a loading box is not told yet. No backfill: this page reads none.
 *
 * @module services/channels/channelChatContext
 */
import {
  freshnessOfState, LOOKER_CHAT_ROW_CAP, type LookerChatContext, type LookerChatTile,
} from '@/services/looker/lookerChatContext';
import type { LookerTileType } from '@/services/looker/lookerTypes';
import { shownCell } from '@/services/overview/boxTableParts';
import type { BoxTable } from '@/services/overview/overviewTables';
import { TOTAL_ROW_IDS, type ChannelPageInput } from './channelTables';

export const CHANNELS_TEMPLATE_ID = 'channels';

const TILE_TYPE: Readonly<Record<string, LookerTileType>> = {
  '2': 'single_value', '3': 'single_value', '4': 'line', '5d': 'bar', ga4: 'single_value', 'ga4-trend': 'line',
};

const rowOf = (b: BoxTable, ri: number, currency: string | null): Record<string, string | null> => {
  const row = b.rows[ri];
  return Object.fromEntries(b.columns.map((col, i) => [col, shownCell(b.kinds[ri]?.[i], row[i], currency)]));
};

/** null = not told yet (loading); a failed or empty box is told, with the words the page shows. */
const chatTileOf = (b: BoxTable, currency: string | null): LookerChatTile | null => {
  if (b.status === 'loading' || b.status === 'needsWorkspace') return null;
  const ok = b.status === 'ok';
  const totalAt = ok && TOTAL_ROW_IDS.has(b.id) && b.rows.length > 0 ? b.rows.length - 1 : -1;
  const body = totalAt < 0 ? b.rows.length : totalAt;
  const shown = Math.min(body, LOOKER_CHAT_ROW_CAP);
  return {
    title: b.title,
    type: TILE_TYPE[b.id] ?? 'table',
    status: b.status,
    columns: b.columns,
    rows: Array.from({ length: shown }, (_, ri) => rowOf(b, ri, currency)),
    rowsOmitted: body - shown,
    moreRowsThanLoaded: false,
    ...(totalAt >= 0 ? { totals: rowOf(b, totalAt, currency) } : {}),
    ...(b.notes.length > 0 ? { coverageNotes: b.notes } : {}),
    ...(ok && b.resultThrough ? { resultThrough: b.resultThrough } : {}),
    ...(ok && b.resultThrough && b.resultPrevious ? { resultPrevious: { start: b.resultPrevious.start, end: b.resultPrevious.end } } : {}),
  };
};

export const buildChannelChatContext = (input: ChannelPageInput): LookerChatContext => {
  const w = input.windows;
  const currency = input.money?.currency ?? null;
  return {
    ...freshnessOfState(input.freshness),
    kind: 'looker',
    template: { id: CHANNELS_TEMPLATE_ID, title: `${input.title} · ${input.channelLabel}` },
    scope: input.allWorkspaces ? { allWorkspaces: true, workspaceName: null } : { allWorkspaces: false, workspaceName: input.workspaceName },
    filters: input.filters,
    period: { start: w.current.start, end: w.current.end, previousStart: w.previous.start, previousEnd: w.previous.end },
    latestDataDate: input.latestDataDate,
    currency,
    // As the page's currency line, with no currency too: then no account that spent has one, so what it filters is empty.
    ...(input.money?.unknownExcluded ? { currencyUnknownExcluded: true as const } : {}),
    tiles: input.tables.flatMap((b) => {
      const tile = chatTileOf(b, currency);
      return tile ? [tile] : [];
    }),
  };
};
